// M50 (2026-04-25) — Admin AI Tuning page.
//
// 4 sections:
//   1. Pipeline hiện tại (SVG visualization)
//   2. Tham số điều khiển (sliders)
//   3. Preset đề xuất
//   4. Đề xuất mô hình thử nghiệm
//
// Gọi 3 endpoints:
//   GET /admin/ai-tuning
//   PATCH /admin/ai-tuning
//   POST /admin/ai-tuning/apply-preset

import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/auth.js';
import { showConfirm } from '../lib/dialogs.js';

export default function AdminAiTuningPage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146: manager với quyền 'ai_tuning' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('ai_tuning');
    return false;
  })();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  // Local edit state — cho phép user edit slider rồi bấm lưu
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin được truy cập trang này');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminAiTuning();
      setData(res);
      setDraft(res.tuning);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được cấu hình');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function saveDraft() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.adminAiTuningUpdate(draft);
      toast.success('Đã lưu tham số');
      await load();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  }

  // M112 (2026-04-26): User profiles handlers (thay applyPreset cũ)
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalInitial, setProfileModalInitial] = useState(null);  // null = create, {id,...} = edit

  function openSaveProfile() {
    setProfileModalInitial(null);  // create new
    setProfileModalOpen(true);
  }
  function openEditProfile(profile) {
    setProfileModalInitial(profile);
    setProfileModalOpen(true);
  }

  async function handleSaveProfile({ id, name, description, params }) {
    setSaving(true);
    try {
      if (id) {
        await api.adminAiTuningProfileUpdate(id, { name, description, params });
        toast.success(`✓ Đã sửa profile "${name}"`);
      } else {
        await api.adminAiTuningProfileCreate({ name, description, params });
        toast.success(`✓ Đã tạo profile "${name}"`);
      }
      setProfileModalOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Lưu profile thất bại');
    } finally {
      setSaving(false);
    }
  }

  async function applyProfile(profile) {
    const ok = await showConfirm({
      title: `Áp profile "${profile.name}"?`,
      message: `${profile.description || 'Áp dụng cấu hình này vào AI hiện tại?'}`,
      confirmText: 'Áp dụng',
      cancelText: 'Huỷ',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.adminAiTuningProfileApply(profile.id);
      toast.success(`✓ Đã áp profile "${profile.name}"`);
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Áp profile thất bại');
    } finally {
      setSaving(false);
    }
  }

  async function deleteProfileHandler(profile) {
    const ok = await showConfirm({
      title: `Xoá profile "${profile.name}"?`,
      message: 'Hành động này không thể undo. Settings AI hiện tại không bị ảnh hưởng — chỉ profile bị xoá.',
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.adminAiTuningProfileDelete(profile.id);
      toast.success(`Đã xoá profile "${profile.name}"`);
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Xoá profile thất bại');
    }
  }

  if (loading || !data) {
    return (
      <div className="min-h-full bg-slate-50 py-8 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="p-6 text-center text-slate-500 text-sm">Đang tải…</div>
        </div>
      </div>
    );
  }

  // M112 (2026-04-26): bỏ presets cứng, dùng profiles user-defined
  const { tuning, spec, routing, profiles = [], model_recommendations, active_profile_id } = data;

  // Dirty = draft khác với tuning đã lưu
  const isDirty = JSON.stringify(draft) !== JSON.stringify(tuning);

  return (
    <div className="min-h-full bg-gradient-to-br from-sky-50/60 via-white to-indigo-50/40 py-8 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        {/* Back */}
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700 inline-block mb-4">← Trang admin</Link>

        {/* Header */}
        <header className="mb-6 bg-white rounded-2xl border border-sky-100 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-2xl shadow-md">
            🎛
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-slate-800">Tuning AI</h1>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Điều khiển tham số, xem mô hình hiện tại, áp preset hoặc thử nghiệm mô hình mạnh hơn.
            </p>
          </div>
          {active_profile_id && (
            <div className="text-xs px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
              Đang dùng profile: {profiles.find(p => p.id === active_profile_id)?.name}
            </div>
          )}
        </header>

        {/* ===== SECTION 1: Pipeline hiện tại ===== */}
        <section className="mb-6">
          <SectionTitle icon="🎨" title="Mô hình hiện tại (pipeline)" desc="Sơ đồ luồng xử lý câu hỏi với tham số thực đang áp dụng" />
          <PipelineDiagram
            tuning={tuning}
            routing={routing}
          />
        </section>

        {/* ===== SECTION 1.5 (M180, 2026-05-23): Step 1 thresholds ===== */}
        <section className="mb-6">
          <SectionTitle
            icon="🎯"
            title="Bước 1 — Khớp câu hỏi sẵn có"
            desc='Khi NV hỏi, app dùng pgvector tính độ giống với kho câu hỏi đã duyệt. Tuỳ điểm cao/thấp mà ra "Trả lời tự động" / "Gợi ý câu gần giống" / "Không khớp → gọi AI". KHÔNG ảnh hưởng cost AI ở bước này (chỉ tính cosine local).'
          />
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <ParamGroupHeader title="📊 Ngưỡng phân loại Step 1" />
            <SliderRow
              spec={spec.step1.auto_answer}
              value={draft.step1?.auto_answer ?? spec.step1.auto_answer.default}
              onChange={(v) => setDraft({ ...draft, step1: { ...(draft.step1 || {}), auto_answer: v } })}
              isFloat
            />
            <div className="text-[11px] text-slate-500 -mt-1 mb-2 pl-1">
              Score ≥ ngưỡng này → trả thẳng đáp án có sẵn. <b>Cao</b> = an toàn (ít trả sai),
              <b> thấp</b> = quyết đoán hơn. <i>Default 0.92.</i>
            </div>

            <SliderRow
              spec={spec.step1.candidate}
              value={draft.step1?.candidate ?? spec.step1.candidate.default}
              onChange={(v) => setDraft({ ...draft, step1: { ...(draft.step1 || {}), candidate: v } })}
              isFloat
            />
            <div className="text-[11px] text-slate-500 -mt-1 mb-2 pl-1">
              Score ≥ ngưỡng này (nhưng &lt; ngưỡng "Trả lời tự động") → gợi ý top 5 câu gần giống cho NV chọn.
              <b> Thấp</b> = phủ nhiều câu hơn, <b>cao</b> = chỉ gợi câu rất giống. <i>Default 0.70.</i>
            </div>

            <SliderRow
              spec={spec.step1.min_match}
              value={draft.step1?.min_match ?? spec.step1.min_match.default}
              onChange={(v) => setDraft({ ...draft, step1: { ...(draft.step1 || {}), min_match: v } })}
              isFloat
            />
            <div className="text-[11px] text-slate-500 -mt-1 mb-2 pl-1">
              Score dưới ngưỡng này → "Không khớp" → app gọi AI sinh đáp án mới (Step 2 RAG). <i>Default 0.60.</i>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
              ⚠️ <b>Lưu ý thứ tự ngưỡng:</b> <code className="px-1 bg-white/60 rounded">auto_answer &gt; candidate &gt; min_match</code>.
              Sai thứ tự thì logic vẫn chạy nhưng NV có thể thấy kết quả lạ.
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3 flex-wrap">
              <button
                onClick={saveDraft}
                disabled={saving || !isDirty}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold shadow transition"
              >
                {saving ? '⏳ Đang lưu…' : isDirty ? '💾 Lưu thay đổi' : '✓ Đã lưu'}
              </button>
              <button
                onClick={() => setDraft(tuning)}
                disabled={!isDirty || saving}
                className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-600 text-sm"
              >
                ↺ Reset về giá trị đã lưu
              </button>
              {isDirty && <span className="text-xs text-amber-600 font-semibold">● Có thay đổi chưa lưu</span>}
            </div>
          </div>
        </section>

        {/* ===== SECTION 2: Tham số ===== */}
        <section className="mb-6">
          <SectionTitle icon="⚙️" title="Tham số điều khiển (Bước 2 — RAG)" desc='Áp dụng cho Step 2 (khi Step 1 "Không khớp", app gọi AI sinh đáp án mới từ KB+Rules+Q). Chỉnh slider rồi bấm Lưu, áp dụng tức thì.' />
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            {/* M109 (2026-04-26): Mode toggle Unified vs Split */}
            <ParamGroupHeader title="🔀 Chế độ retrieve" />
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDraft({ ...draft, mode: 'unified' })}
                className={`text-left p-3 rounded-lg border-2 transition ${
                  (draft.mode || 'unified') === 'unified'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <div className="text-sm font-semibold text-slate-800">⚡ Unified (recommend)</div>
                <div className="text-[11px] text-slate-600 mt-1">
                  Gộp 3 nguồn → top K chung. Tự động phân bổ theo similarity. Tiết kiệm 50-60% token.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setDraft({ ...draft, mode: 'split' })}
                className={`text-left p-3 rounded-lg border-2 transition ${
                  (draft.mode || 'unified') === 'split'
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <div className="text-sm font-semibold text-slate-800">🔢 Split (legacy)</div>
                <div className="text-[11px] text-slate-600 mt-1">
                  Top K riêng cho mỗi nguồn. Đảm bảo quota. Tốn token hơn khi câu hỏi 1-domain.
                </div>
              </button>
            </div>

            {(draft.mode || 'unified') === 'unified' ? (
              <>
                <ParamGroupHeader title="📊 Tham số Unified" />
                <SliderRow
                  spec={spec.unified.top}
                  value={draft.unified?.top ?? spec.unified.top.default}
                  onChange={(v) => setDraft({ ...draft, unified: { ...(draft.unified || {}), top: v } })}
                />
                <SliderRow
                  spec={spec.unified.min_match}
                  value={draft.unified?.min_match ?? spec.unified.min_match.default}
                  onChange={(v) => setDraft({ ...draft, unified: { ...(draft.unified || {}), min_match: v } })}
                  isFloat
                />
                <ParamGroupHeader title="📢 Prompts (luôn dùng cho cả 2 mode)" />
                <SliderRow
                  spec={spec.retrieve.top_prompts}
                  value={draft.retrieve.top_prompts}
                  onChange={(v) => setDraft({ ...draft, retrieve: { ...draft.retrieve, top_prompts: v } })}
                />
              </>
            ) : (
              <>
                <ParamGroupHeader title="📋 Số lượng lấy (retrieve counts) — Split mode" />
                {/* M114 (2026-04-26): bỏ related_top khỏi loop AI params — đó là UI display, không phải retrieve count. Đã move sang section riêng "Hiển thị UI". */}
                {Object.entries(spec.retrieve).filter(([k]) => k !== 'related_top').map(([k, s]) => (
                  <SliderRow
                    key={k}
                    spec={s}
                    value={draft.retrieve[k]}
                    onChange={(v) => setDraft({ ...draft, retrieve: { ...draft.retrieve, [k]: v } })}
                  />
                ))}
                <ParamGroupHeader title="🎯 Ngưỡng tương đồng (min score cosine) — Split mode" />
                {Object.entries(spec.minScore).map(([k, s]) => (
                  <SliderRow
                    key={k}
                    spec={s}
                    value={draft.minScore[k]}
                    onChange={(v) => setDraft({ ...draft, minScore: { ...draft.minScore, [k]: v } })}
                    isFloat
                  />
                ))}
              </>
            )}

            <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3 flex-wrap">
              <button
                onClick={saveDraft}
                disabled={saving || !isDirty}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold shadow transition"
              >
                {saving ? '⏳ Đang lưu…' : isDirty ? '💾 Lưu thay đổi' : '✓ Đã lưu'}
              </button>
              <button
                onClick={() => setDraft(tuning)}
                disabled={!isDirty || saving}
                className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-600 text-sm"
              >
                ↺ Reset về giá trị đã lưu
              </button>
              {isDirty && <span className="text-xs text-amber-600 font-semibold">● Có thay đổi chưa lưu</span>}
            </div>

            <div className="mt-4 p-3 rounded-lg bg-sky-50 border border-sky-100 text-xs text-sky-800">
              💡 <b>LLM params</b> (temperature, max_tokens) quản lý tại <Link to="/admin/ai" className="underline font-semibold">Cài đặt AI → Routing</Link>.
            </div>
          </div>
        </section>

        {/* ===== SECTION 2.5 (M114, 2026-04-26): Hiển thị UI hỏi đáp — KHÔNG ảnh hưởng AI ===== */}
        <section className="mb-6">
          <SectionTitle icon="🎨" title="Hiển thị giao diện Hỏi đáp" desc="Số lượng items hiển thị cho NV trên trang /ask. KHÔNG ảnh hưởng cosine retrieve, AI cost, hay quality câu trả lời — chỉ là số UI hiển thị." />
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            {spec.retrieve.related_top && (
              <SliderRow
                spec={spec.retrieve.related_top}
                value={draft.retrieve.related_top ?? 5}
                onChange={(v) => setDraft({ ...draft, retrieve: { ...draft.retrieve, related_top: v } })}
              />
            )}
            <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
              ℹ️ <b>Lưu ý</b>: AI vẫn luôn so sánh với top 10 câu hỏi gần nhất (cứng) — setting này chỉ kiểm soát số NV thấy trong UI. Auto_answer luôn hiện cap 3 nguồn (giữ nguyên — gọn cho NV nhìn).
            </div>
          </div>
        </section>

        {/* ===== SECTION 3: M112 (2026-04-26) — Profiles của tôi (thay 5 preset cứng) ===== */}
        <section className="mb-6">
          <div className="flex items-end justify-between gap-2 mb-3 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <span>📚</span><span>Profiles của tôi</span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Lưu cấu hình hiện tại thành profile để A/B test, theo dõi chi phí, thử nghiệm.</p>
            </div>
            <button
              type="button"
              onClick={() => openSaveProfile()}
              className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            >
              💾 Lưu cấu hình hiện tại
            </button>
          </div>

          {profiles.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">
              Chưa có profile nào. Bấm <strong>💾 Lưu cấu hình hiện tại</strong> ở trên để tạo profile đầu tiên.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {profiles.map(p => (
                <ProfileCard
                  key={p.id}
                  profile={p}
                  active={p.id === active_profile_id}
                  busy={saving}
                  onApply={() => applyProfile(p)}
                  onEdit={() => openEditProfile(p)}
                  onDelete={() => deleteProfileHandler(p)}
                />
              ))}
            </div>
          )}
        </section>

        {/* M112: Modal Save/Edit profile */}
        {profileModalOpen && (
          <ProfileModal
            initial={profileModalInitial}
            onClose={() => setProfileModalOpen(false)}
            onSave={handleSaveProfile}
            currentTuning={draft}
          />
        )}

        {/* ===== SECTION 4: Đề xuất mô hình ===== */}
        <section className="mb-6">
          <SectionTitle icon="🔬" title="Đề xuất mô hình thử nghiệm" desc="Model mạnh hơn để tăng chất lượng — kèm ước lượng chi phí." />
          <div className="space-y-3">
            {model_recommendations.map((m, i) => (
              <ModelRecCard key={i} rec={m} />
            ))}
          </div>
          <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
            ⚠️ <b>Để đổi model:</b> vào <Link to="/admin/ai" className="underline font-semibold">Cài đặt AI → Routing</Link>, chọn task (ask_ai/chat_ai/embedding), đổi primary_provider + primary_model → Lưu. Sau đó có thể cần rebuild embeddings (nếu đổi model embedding) — có nút "Rebuild all" trong trang đó.
          </div>
        </section>
      </div>
    </div>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

function SectionTitle({ icon, title, desc }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
        <span>{icon}</span>
        <span>{title}</span>
      </h2>
      {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
    </div>
  );
}

function ParamGroupHeader({ title }) {
  return (
    <div className="mt-4 first:mt-0 mb-2 text-xs font-bold text-slate-600 uppercase tracking-wide">
      {title}
    </div>
  );
}

function SliderRow({ spec, value, onChange, isFloat = false }) {
  const step = isFloat ? 0.05 : 1;
  const pct = ((value - spec.min) / (spec.max - spec.min)) * 100;
  const display = isFloat ? value.toFixed(2) : String(value);
  const defDisplay = isFloat ? spec.default.toFixed(2) : String(spec.default);
  const isDefault = Math.abs(value - spec.default) < 0.001;

  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
        <label className="text-sm font-medium text-slate-700">{spec.label}</label>
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono font-bold tabular-nums text-indigo-600">{display}</span>
          {!isDefault && (
            <span className="text-slate-400">(default: {defDisplay})</span>
          )}
        </div>
      </div>
      <input
        type="range"
        min={spec.min}
        max={spec.max}
        step={step}
        value={value}
        onChange={(e) => onChange(isFloat ? Number(e.target.value) : parseInt(e.target.value, 10))}
        className="w-full accent-indigo-500 cursor-pointer"
        style={{
          background: `linear-gradient(to right, rgb(99 102 241) 0%, rgb(99 102 241) ${pct}%, rgb(226 232 240) ${pct}%, rgb(226 232 240) 100%)`,
        }}
      />
      <div className="flex justify-between text-[10px] text-slate-400 mt-0.5 font-mono tabular-nums">
        <span>{isFloat ? spec.min.toFixed(2) : spec.min}</span>
        <span>{isFloat ? spec.max.toFixed(2) : spec.max}</span>
      </div>
    </div>
  );
}

// M112 (2026-04-26) — ProfileCard: thay thế PresetCard. Mỗi profile do admin tạo.
function ProfileCard({ profile, active, busy, onApply, onEdit, onDelete }) {
  const params = profile.params || {};
  const mode = params.mode || 'unified';
  return (
    <div className={`rounded-2xl border p-4 transition ${active ? 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-300 shadow-sm ring-2 ring-indigo-100' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-slate-800 break-words">{profile.name}</h3>
            {active && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-indigo-600 text-white">
                ✓ Đang dùng
              </span>
            )}
          </div>
          {profile.description && (
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{profile.description}</p>
          )}
          <div className="text-[10px] text-slate-400 mt-1">
            Cập nhật: {profile.updated_at?.slice(0, 16).replace('T', ' ')}
          </div>
        </div>
      </div>

      {/* Params summary */}
      <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] font-mono tabular-nums text-slate-600">
        {mode === 'unified' ? (
          <div className="space-y-0.5">
            <div className="text-emerald-700 font-semibold">⚡ Unified mode</div>
            <div>Top: <strong>{params.unified?.top ?? 10}</strong> chunks · Min: <strong>{(params.unified?.min_match ?? 0.55).toFixed(2)}</strong></div>
            <div>Prompts: <strong>{params.retrieve?.top_prompts ?? 2}</strong></div>
          </div>
        ) : (
          <div>
            <div className="text-amber-700 font-semibold mb-1">🔢 Split mode</div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <div>Q: top {params.retrieve?.top_questions ?? '-'}</div>
              <div>min {(params.minScore?.questions ?? 0).toFixed(2)}</div>
              <div>KB: top {params.retrieve?.top_knowledge ?? '-'}</div>
              <div>min {(params.minScore?.knowledge ?? 0).toFixed(2)}</div>
              <div>Rules: top {params.retrieve?.top_rules ?? '-'}</div>
              <div>min {(params.minScore?.rules ?? 0).toFixed(2)}</div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <button
          onClick={onApply}
          disabled={busy || active}
          className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
            active
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-60'
          }`}
        >
          {active ? 'Đang dùng' : '✨ Áp dụng'}
        </button>
        <button
          onClick={onEdit}
          disabled={busy}
          className="px-3 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50"
          title="Sửa tên/mô tả/params"
        >
          ✏️
        </button>
        <button
          onClick={onDelete}
          disabled={busy}
          className="px-3 py-2 rounded-lg text-sm bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 disabled:opacity-50"
          title="Xoá profile"
        >
          🗑
        </button>
      </div>
    </div>
  );
}

// M112 (2026-04-26) — Modal save/edit profile
function ProfileModal({ initial, onClose, onSave, currentTuning }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [busy, setBusy] = useState(false);
  // Params: nếu edit → dùng params của profile; nếu create → dùng tuning hiện tại (currentTuning)
  const params = isEdit ? (initial.params || currentTuning) : currentTuning;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Tên profile không được rỗng'); return; }
    setBusy(true);
    try {
      await onSave({ id: initial?.id, name: name.trim(), description: description.trim(), params });
    } finally { setBusy(false); }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6"
      >
        <h3 className="text-lg font-semibold mb-4">
          {isEdit ? `✏️ Sửa profile "${initial.name}"` : '💾 Lưu cấu hình hiện tại thành profile'}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Tên profile *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={200}
              required
              placeholder="VD: Test SP men vi sinh, Cấu hình cuối tuần..."
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Ghi chú (optional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Lý do tạo profile này, dùng cho test gì..."
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm resize-none"
            />
          </div>
          {/* Preview params */}
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] font-mono text-slate-600">
            <div className="text-xs font-semibold text-slate-700 mb-1">Cấu hình sẽ lưu:</div>
            <div>Mode: <strong>{params.mode || 'unified'}</strong></div>
            {(params.mode || 'unified') === 'unified' ? (
              <>
                <div>Unified: top <strong>{params.unified?.top ?? 10}</strong>, min <strong>{(params.unified?.min_match ?? 0.55).toFixed(2)}</strong></div>
                <div>Prompts: <strong>{params.retrieve?.top_prompts ?? 2}</strong></div>
              </>
            ) : (
              <>
                <div>Q: top {params.retrieve?.top_questions} · min {params.minScore?.questions?.toFixed(2)}</div>
                <div>KB: top {params.retrieve?.top_knowledge} · min {params.minScore?.knowledge?.toFixed(2)}</div>
                <div>Rules: top {params.retrieve?.top_rules} · min {params.minScore?.rules?.toFixed(2)}</div>
              </>
            )}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
          >
            Huỷ
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            {busy ? '⏳ Đang lưu…' : isEdit ? 'Lưu thay đổi' : '💾 Tạo profile'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ModelRecCard({ rec }) {
  // Tailwind JIT không bắt dynamic class `bg-${x}-50` → hardcode toàn bộ
  const typeMap = {
    embedding: {
      label: 'Embedding', icon: '🔗',
      iconBg: 'bg-cyan-50', badgeBg: 'bg-cyan-100', badgeText: 'text-cyan-700',
    },
    llm_ask: {
      label: 'LLM · Hỏi đáp', icon: '🤖',
      iconBg: 'bg-violet-50', badgeBg: 'bg-violet-100', badgeText: 'text-violet-700',
    },
    llm_chat: {
      label: 'LLM · Chat', icon: '💬',
      iconBg: 'bg-fuchsia-50', badgeBg: 'bg-fuchsia-100', badgeText: 'text-fuchsia-700',
    },
  };
  const t = typeMap[rec.type] || {
    label: rec.type, icon: '•',
    iconBg: 'bg-slate-50', badgeBg: 'bg-slate-100', badgeText: 'text-slate-700',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start gap-4">
      <div className={`w-11 h-11 shrink-0 rounded-xl ${t.iconBg} flex items-center justify-center text-xl`}>
        {t.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${t.badgeBg} ${t.badgeText}`}>
            {t.label}
          </span>
          <h3 className="font-bold text-slate-800 font-mono text-sm">{rec.provider} · {rec.model}</h3>
          {rec.dims && <span className="text-[11px] text-slate-500">· {rec.dims} dims</span>}
        </div>
        <div className="text-xs text-slate-600 space-y-0.5">
          <div>💰 <b>Giá:</b> {rec.price_note}</div>
          <div>✨ <b>Chất lượng:</b> {rec.quality_note}</div>
          <div>🎯 <b>Khi nào dùng:</b> {rec.when}</div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Pipeline SVG diagram
// =====================================================================
function PipelineDiagram({ tuning, routing }) {
  const embRouting = routing.embedding || {};
  const askRouting = routing.ask_ai || {};
  const chatRouting = routing.chat_ai || {};

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 overflow-x-auto">
      <div className="min-w-[700px]">
        {/* Step 1: User question */}
        <PipelineBox
          icon="💬"
          label="Câu hỏi NV"
          sub="Text + (optional) ảnh"
          color="slate"
          center
        />
        <Arrow />

        {/* Step 2: Embedding */}
        <PipelineBox
          icon="🔗"
          label="Embedding"
          sub={`${embRouting.primary_provider || '?'} · ${embRouting.primary_model || '?'}`}
          color="cyan"
          center
        />
        <Arrow />

        {/* Step 3: Retrieve — M109 (2026-04-26) hiện theo mode. M110: default 'unified' khi mode undefined. */}
        {(tuning.mode || 'unified') === 'unified' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-2">
            <PipelineBox
              icon="⚡"
              label={`Top ${tuning.unified?.top ?? 10} unified (3 nguồn)`}
              sub={`min ${(tuning.unified?.min_match ?? 0.55).toFixed(2)} · pool Q+KB+Rules`}
              color="emerald"
            />
            <PipelineBox
              icon="📢"
              label={`Top ${tuning.retrieve.top_prompts} prompts`}
              sub="hint bổ trợ"
              color="purple"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 my-2">
            <PipelineBox
              icon="📋"
              label={`Top ${tuning.retrieve.top_questions} câu hỏi`}
              sub={`min ${tuning.minScore.questions.toFixed(2)}`}
              color="emerald"
            />
            <PipelineBox
              icon="📚"
              label={`Top ${tuning.retrieve.top_knowledge} KB`}
              sub={`min ${tuning.minScore.knowledge.toFixed(2)}`}
              color="amber"
            />
            <PipelineBox
              icon="📜"
              label={`Top ${tuning.retrieve.top_rules} nội quy`}
              sub={`min ${tuning.minScore.rules.toFixed(2)}`}
              color="rose"
            />
            <PipelineBox
              icon="📢"
              label={`Top ${tuning.retrieve.top_prompts} prompts`}
              sub="hint bổ trợ"
              color="purple"
            />
          </div>
        )}
        <Arrow />

        {/* Step 4: LLM */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-2">
          <PipelineBox
            icon="🤖"
            label={`LLM · Ask AI`}
            sub={`${askRouting.primary_provider || '?'} · ${askRouting.primary_model || '?'}${askRouting.temperature != null ? ` · temp ${askRouting.temperature}` : ''}${askRouting.max_tokens ? ` · max ${askRouting.max_tokens}` : ''}`}
            color="violet"
          />
          <PipelineBox
            icon="💬"
            label="LLM · Chat AI"
            sub={`${chatRouting.primary_provider || '?'} · ${chatRouting.primary_model || '?'}${chatRouting.temperature != null ? ` · temp ${chatRouting.temperature}` : ''}${chatRouting.max_tokens ? ` · max ${chatRouting.max_tokens}` : ''}`}
            color="fuchsia"
          />
        </div>
        <Arrow />

        {/* Step 5: Answer */}
        <PipelineBox
          icon="✨"
          label="Câu trả lời"
          sub="Markdown + emoji + sources"
          color="indigo"
          center
        />
      </div>
    </div>
  );
}

function PipelineBox({ icon, label, sub, color = 'slate', center = false }) {
  // Tailwind cần class cụ thể — dùng map để tránh JIT miss
  const colors = {
    slate:    { bg: 'bg-slate-50',    border: 'border-slate-200',   text: 'text-slate-700' },
    cyan:     { bg: 'bg-cyan-50',     border: 'border-cyan-200',    text: 'text-cyan-800' },
    emerald:  { bg: 'bg-emerald-50',  border: 'border-emerald-200', text: 'text-emerald-800' },
    amber:    { bg: 'bg-amber-50',    border: 'border-amber-200',   text: 'text-amber-800' },
    rose:     { bg: 'bg-rose-50',     border: 'border-rose-200',    text: 'text-rose-800' },
    purple:   { bg: 'bg-purple-50',   border: 'border-purple-200',  text: 'text-purple-800' },
    violet:   { bg: 'bg-violet-50',   border: 'border-violet-200',  text: 'text-violet-800' },
    fuchsia:  { bg: 'bg-fuchsia-50',  border: 'border-fuchsia-200', text: 'text-fuchsia-800' },
    indigo:   { bg: 'bg-indigo-50',   border: 'border-indigo-200',  text: 'text-indigo-800' },
  };
  const c = colors[color] || colors.slate;

  return (
    <div className={`${c.bg} ${c.border} border-2 rounded-xl px-3 py-2.5 ${center ? 'max-w-sm mx-auto' : ''} shadow-sm`}>
      <div className="flex items-center gap-2">
        <span className="text-xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-bold ${c.text}`}>{label}</div>
          {sub && <div className="text-[11px] text-slate-500 font-mono truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex justify-center my-1 text-slate-300 text-xl">↓</div>
  );
}
